import { Loader2 } from 'lucide-react';
import { Pagination } from './Pagination';
import type { PagedListControls } from './usePagedList';

/**
 * The end of a list, in whichever mode it is in: page numbers, or the
 * sentinel that loads the next page as it comes into view.
 *
 * Scroll mode also offers a "Load more" button — for keyboard users, and for
 * the odd layout where the sentinel never crosses into view.
 */
export function ListFooter({ list }: { list: PagedListControls }) {
	const { pageInfo } = list;
	if (!pageInfo) return null;

	if (list.mode === 'pages') {
		return (
			<Pagination
				currentPage={pageInfo.currentPage}
				totalPages={pageInfo.totalPages}
				totalCount={pageInfo.totalCount}
				hasNextPage={pageInfo.hasNextPage}
				hasPreviousPage={pageInfo.hasPreviousPage}
				onPageChange={list.setPage}
				limit={list.limit}
			/>
		);
	}

	// Same rule as Pagination: a list that fits on one page has no footer.
	if (pageInfo.totalCount <= list.limit) return null;

	return (
		<div className="flex flex-col items-center gap-3 pt-4">
			<div ref={list.sentinelRef} aria-hidden className="h-px w-full" />
			{list.loadingMore ? (
				<Loader2 size={16} className="animate-spin text-text-4" />
			) : list.hasMore ? (
				<button
					onClick={list.loadMore}
					className="cursor-pointer rounded-md px-3 py-1 text-sm text-text-3 transition-colors hover:bg-surface-hover hover:text-text-1"
				>
					Load more
				</button>
			) : null}
			<span className="text-sm text-text-4">
				{list.loadedCount} of {pageInfo.totalCount}
			</span>
		</div>
	);
}
